/**
 * File Service - API methods for file storage
 * Handles generic file uploads and profile-picture management.
 * Signed URLs arrive pre-baked in API responses — the FE never constructs them.
 */

import apiClient from './apiClient';

// ── Response types ──────────────────────────────────────────────────────────

/** Returned by POST /files — opaque UUID for the stored asset. */
export interface UploadFileResponse {
  id: string;
}

/** Returned by POST /auth/me/profile-picture and DELETE /auth/me/profile-picture. */
export interface ProfilePictureResponse {
  profilePic: string | null;
}

// ── Admin file-asset management types ────────────────────────────────────────

export type FileAssetSource = 'UPLOADED' | 'GENERATED';
export type FileProcessingStatus = 'PENDING' | 'READY' | 'FAILED';
export type FileScanStatus = 'PENDING' | 'CLEAN' | 'QUARANTINED';

/** A single row in the super-admin file-asset list. */
export interface FileAssetListItem {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number | null;
  source: FileAssetSource;
  processingStatus: FileProcessingStatus;
  scanStatus: FileScanStatus;
  createdAt: string;
  organization: { id: string; name: string } | null;
  createdBy: { id: string; firstName: string; lastName: string; email: string } | null;
  /** Pre-signed, expiring download URL (server-generated). */
  downloadUrl: string;
}

export interface ListFileAssetsParams {
  page?: number;
  limit?: number;
  search?: string;
  processingStatus?: FileProcessingStatus;
  source?: FileAssetSource;
  organizationId?: string;
}

export interface PaginatedFileAssets {
  data: FileAssetListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ── Generic file upload ─────────────────────────────────────────────────────

/**
 * Upload a single file to the platform storage.
 *
 * POST /files  (multipart `file`, JWT-authenticated)
 * Returns the UUID of the created FileAsset — callers persist this id on
 * their own domain model field (e.g. `profilePicFileId`).
 *
 * Signed-URL strings are produced server-side and arrive embedded in API
 * responses (e.g. `/auth/me` → `profilePic`).  Never call this to "get a URL".
 */
export const uploadFile = async (file: File): Promise<UploadFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<UploadFileResponse>('/api/v1/files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

// ── Profile-picture endpoints ───────────────────────────────────────────────

/**
 * Upload a new profile picture for the current user.
 *
 * POST /auth/me/profile-picture  (multipart `file`, JWT)
 * Returns the fresh signed URL for the new avatar so the caller can update
 * the auth store without an extra /auth/me round-trip (though calling
 * `authStore.refreshUser()` afterwards is the canonical pattern).
 */
export const uploadProfilePicture = async (file: File): Promise<ProfilePictureResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ProfilePictureResponse>(
    '/api/v1/auth/me/profile-picture',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );

  return response.data;
};

/**
 * Delete the current user's profile picture.
 *
 * DELETE /auth/me/profile-picture  (JWT)
 * Returns `{ profilePic: null }` confirming the picture has been removed.
 */
export const deleteProfilePicture = async (): Promise<ProfilePictureResponse> => {
  const response = await apiClient.delete<ProfilePictureResponse>(
    '/api/v1/auth/me/profile-picture',
  );

  return response.data;
};

// ── Admin file-asset management (Super Admin) ───────────────────────────────

/**
 * List file assets across all organizations (super-admin console).
 *
 * GET /admin/files  (JWT + SuperAdminGuard)
 * Returns a paginated list; each row carries a pre-signed `downloadUrl`.
 */
export const listFileAssets = async (
  params: ListFileAssetsParams = {},
): Promise<PaginatedFileAssets> => {
  const response = await apiClient.get<PaginatedFileAssets>('/api/v1/admin/files', { params });
  return response.data;
};

/**
 * Delete a file asset by id.
 *
 * DELETE /files/:id  (JWT; super admins authorized for any org)
 * Removes the blob from storage and the FileAsset row.
 */
export const deleteFileAsset = async (id: string): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(`/api/v1/files/${id}`);
  return response.data;
};

const fileService = {
  uploadFile,
  uploadProfilePicture,
  deleteProfilePicture,
  listFileAssets,
  deleteFileAsset,
};

export default fileService;
